import { Check, ChevronRight, ListFilter, RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlays";
import { useFolderTree } from "@/hooks/queries";
import {
  CATEGORY_LABEL,
  NOTE_CATEGORIES,
  type FolderOut,
  type NoteCategory,
  type NoteSort,
} from "@/lib/api";
import { CATEGORY_COLOR, folderColor } from "@/lib/palette";
import { cn } from "@/lib/utils";

import { useHome } from "./home-store";

const SORTS: { value: NoteSort; label: string }[] = [
  { value: "recent", label: "Newest" },
  { value: "alpha", label: "A–Z" },
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 pb-1.5 pt-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * One folder row. Recurses, so an arbitrarily deep hierarchy is navigable —
 * built as a real tree rather than a flat select, because a folder path IS a
 * tree and flattening it loses the parent that gives a name its meaning
 * (`Research / Interpretability` vs a bare `Interpretability`).
 */
function FolderRow({
  folder,
  depth,
  selected,
  onSelect,
}: {
  folder: FolderOut;
  depth: number;
  selected: { category: NoteCategory | null; path: string };
  onSelect: (category: NoteCategory, path: string) => void;
}) {
  const [open, setOpen] = useState(depth === 0);
  const isSelected = selected.category === folder.category && selected.path === folder.path;
  // Guarded for the same reason `FileTree`'s `childrenOf` is: this is a PWA
  // whose cached bundle can meet an API that predates (or postdates) the
  // field, and the filter must not take Home down with it.
  const children = folder.children ?? [];
  const hasChildren = children.length > 0;
  const isRoot = folder.path === "";
  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 rounded pr-1 transition-colors",
          isSelected ? "bg-primary/15" : "hover:bg-accent",
        )}
        style={{ paddingLeft: `${depth * 10}px` }}
      >
        <button
          type="button"
          aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`}
          aria-expanded={hasChildren ? open : undefined}
          disabled={!hasChildren}
          onClick={() => setOpen((v) => !v)}
          className="grid h-6 w-4 shrink-0 place-items-center text-muted-foreground disabled:opacity-0"
        >
          <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
        </button>
        <button
          type="button"
          // A category ROOT is not a folder you can narrow to — narrowing to it
          // is what the category checkbox above already does. It is a heading
          // that opens.
          onClick={() =>
            isRoot ? setOpen((v) => !v) : onSelect(folder.category, isSelected ? "" : folder.path)
          }
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left"
        >
          <span
            aria-hidden
            className="h-[7px] w-[7px] shrink-0 rounded-[2px]"
            style={{
              background: isRoot
                ? (CATEGORY_COLOR[folder.category] ?? "")
                : folderColor(folder.color),
            }}
          />
          <span
            className={cn(
              "truncate text-[12px]",
              isRoot
                ? "font-medium text-foreground/80"
                : isSelected
                  ? "text-foreground"
                  : "text-muted-foreground",
            )}
          >
            {folder.name}
          </span>
          <span className="ml-auto shrink-0 font-mono text-[9px] text-muted-foreground/70">
            {folder.total_count}
          </span>
        </button>
      </div>
      {open && hasChildren && (
        <div>
          {children.map((child) => (
            <FolderRow
              key={`${child.category}:${child.path}`}
              folder={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The homepage's Filter control: sort, category include/exclude, and a folder
 * tree.
 *
 * Everything lives behind this one button because the chosen layout (L1
 * "Ledger") gives the full panel width to cards. What keeps that from becoming
 * invisible modality is the count badge here plus the "Showing…" summary line
 * the page renders beside it.
 *
 * The folder tree lists EVERY category that has folders, not just a
 * currently-selected one. An earlier version only showed folders once exactly
 * one category was ticked, and explained the rule in a sentence — which made
 * the user reason about a constraint instead of picking a folder. Choosing a
 * folder now simply implies its category (the store narrows to it), which is
 * the same outcome reached without the rule.
 */
export function FilterPopover() {
  const [open, setOpen] = useState(false);
  const sort = useHome((s) => s.sort);
  const categories = useHome((s) => s.categories);
  const folder = useHome((s) => s.folder);
  const folderCategory = useHome((s) => s.folderCategory);
  const activeCount = useHome((s) => s.activeFilterCount());
  const tree = useFolderTree();

  // An empty selection means "all four" — that is what the server defaults to,
  // and what every checkbox ticked collapses to.
  const isChecked = (c: NoteCategory) => categories.length === 0 || categories.includes(c);
  const countFor = (c: NoteCategory) =>
    (tree.data?.categories ?? []).find((root) => root.category === c)?.total_count ?? 0;
  const withFolders = (tree.data?.categories ?? []).filter((c) => (c.children ?? []).length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={activeCount > 0 ? "default" : "outline"}
          size="sm"
          aria-label={activeCount > 0 ? `Filter, ${activeCount} active` : "Filter"}
        >
          <ListFilter className="h-3.5 w-3.5" />
          Filter
          {activeCount > 0 && (
            <span className="ml-0.5 font-mono text-[10px] opacity-80">· {activeCount}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[272px] p-2">
        {/* Sort — a segmented control, deliberately NOT the same shape as the
            checkboxes below. The two used to be near-identical rows of circles
            and squares, which made a single-choice control look multi-choice. */}
        <div className="flex items-center gap-2 pb-1.5">
          <SectionLabel>Sort</SectionLabel>
          <div className="ml-auto flex rounded-md border border-border p-0.5">
            {SORTS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => useHome.getState().setSort(option.value)}
                aria-pressed={sort === option.value}
                className={cn(
                  "rounded px-2 py-0.5 text-[11.5px] transition-colors",
                  sort === option.value
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="my-1 h-px bg-border" />

        <SectionLabel>Show</SectionLabel>
        <div className="flex flex-col gap-px pb-1">
          {NOTE_CATEGORIES.map((category) => {
            const checked = isChecked(category);
            return (
              <button
                key={category}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => useHome.getState().toggleCategory(category)}
                className="flex items-center gap-2 rounded px-1 py-1 text-left transition-colors hover:bg-accent"
              >
                <span
                  className={cn(
                    "grid h-[15px] w-[15px] shrink-0 place-items-center rounded border transition-colors",
                    checked ? "border-primary bg-primary" : "border-border bg-input",
                  )}
                >
                  {checked && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={3.5} />
                  )}
                </span>
                <span
                  aria-hidden
                  className="h-[7px] w-[7px] shrink-0 rounded-[2px]"
                  style={{ background: CATEGORY_COLOR[category] ?? "" }}
                />
                <span
                  className={cn(
                    "flex-1 truncate text-[12.5px]",
                    checked ? "text-foreground" : "text-muted-foreground/60",
                  )}
                >
                  {CATEGORY_LABEL[category]}
                </span>
                <span className="shrink-0 font-mono text-[9px] text-muted-foreground/70">
                  {countFor(category)}
                </span>
              </button>
            );
          })}
        </div>

        {withFolders.length > 0 && (
          <>
            <div className="my-1 h-px bg-border" />
            <SectionLabel>Narrow to a folder</SectionLabel>
            <div className="max-h-[220px] overflow-y-auto pb-1">
              {withFolders.map((root) => (
                <FolderRow
                  key={root.category}
                  folder={root}
                  depth={0}
                  selected={{ category: folderCategory, path: folder }}
                  onSelect={(category, path) =>
                    useHome.getState().setFolder(path ? category : null, path)
                  }
                />
              ))}
            </div>
          </>
        )}

        {activeCount > 0 && (
          <>
            <div className="my-1 h-px bg-border" />
            <button
              type="button"
              onClick={() => useHome.getState().clearFilters()}
              className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" /> Reset filters
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
